import { Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Auth0Callback, Auth0DecodedHash, Auth0Error, Auth0ParseHashError, Auth0UserProfile, WebAuth } from 'auth0-js'
import { ReplaySubject } from 'rxjs'
import { environment } from '../../environments/environment'
import { getTimeDifferenceInSeconds } from '../utility'

export interface UserProfile {
    countryCode: string
    email: string
    firstName?: string
    lastName?: string
    sub: string
}

const accessTokenKey = 'authAccessToken'
const idTokenKey = 'authIdToken'
const expiresAtKey = 'authExpiresAt'
const userProfileKey = 'authUserProfile'
const maintainSessionKey = 'authMaintainSession'

@Injectable({
    providedIn: 'root'
})
export class Auth {

    auth: WebAuth
    authenticatedSubject = new ReplaySubject<boolean>(1)
    accessToken: string | undefined | null
    authResult: Auth0DecodedHash
    userProfile: Auth0UserProfile | null
    tokenExpiration: number | null
    authenticated: boolean
    sessionMaintained = false
    renewSessionTimer: ReturnType<typeof setTimeout>
    private testTokenKey = 'token'
    private readonly testToken: string

    constructor(
        private _activatedRoute: ActivatedRoute,
    ) {
        console.log('ENV: ', environment)
        console.log('Auth0 Config:', {
            domain: environment.auth.domain,
            clientID: environment.auth.clientId,
            scope: environment.auth.userScope,
            audience: environment.auth.audience
        })

        this.testToken = sessionStorage.getItem(this.testTokenKey)

        this.auth = new WebAuth({
            domain: environment.auth.domain,
            clientID: environment.auth.clientId,
            scope: environment.auth.userScope,
            audience: environment.auth.audience,
            redirectUri: environment.auth.loginRedirectUri,
        })

        // If 'remember me' was checked values will be in local storage otherwise in session storage. Check both.
        this.accessToken = localStorage.getItem(accessTokenKey) || sessionStorage.getItem(accessTokenKey)
        this.tokenExpiration = JSON.parse(localStorage.getItem(expiresAtKey)) || JSON.parse(sessionStorage.getItem(expiresAtKey))
        this.userProfile = JSON.parse(localStorage.getItem(userProfileKey)) || JSON.parse(sessionStorage.getItem(userProfileKey))
        this.sessionMaintained = localStorage.getItem(maintainSessionKey) === 'true'

        if (this.isAuthenticated()) {
            this.markAsAuthenticated()

            if (this.sessionMaintained) {
                this.startRenewSessionTimer()
            }
        }
        // dont mark as unauthenticated before doing authentication checks
        else if (window.location.pathname !== '/authenticate') {
            this.endSession()
        }
    }

    private getRedirectDomain(): string {
        const currentDomain = window.location.origin
        const isShowcase = currentDomain.includes('showcase.')
        const isLocalhost = currentDomain.includes('localhost')
        
        if (isLocalhost) {
            // During local development, use localhost
            return `${currentDomain}/authenticate`
        }
        
        if (isShowcase) {
            // Maintain showcase subdomain for white-labeled sites
            return `${currentDomain}/authenticate`
        }

        // Default to the configured redirect URI for all other cases
        return environment.auth.loginRedirectUri
    }

    signup(
        signupCredentials: { email: string, password: string },
        successCallback: Auth0Callback<any>,
        errorCallback?: Auth0Callback<Auth0Error>): void {

        this.auth.signup({
            email: signupCredentials.email,
            password: signupCredentials.password,
            connection: environment.auth.realm
        },
        (error: Auth0Error | null, result: any) => {

            if (error) {
                console.log('AUTH: Sign up error: ', error)
                if (errorCallback) {
                    errorCallback(error, result)
                }
                return
            }

            console.log('AUTH: Sign up successful: ', result)
            if (successCallback) {
                successCallback(result, result)
            }
        })
    }

    login(loginCredentials: { email: string, password: string }, errorCallback?: Auth0Callback<Auth0Error>): void {
        const redirectUri = this.getRedirectDomain()
        let options = {
            email: loginCredentials.email,
            password: loginCredentials.password,
            realm: environment.auth.realm,
            responseType: environment.auth.loginResponseType,
            redirectUri: redirectUri
        }
        this.auth.login(options,
            (error: Auth0Error | null, result: any) => {
                if (error) {
                    console.log('AUTH: Login error: ', error)
                    if (errorCallback) {
                        errorCallback(error, result)
                    }
                    return
                }
                console.log('AUTH: Login successful: ', result)
                // Will redirect to authenticate route here

            })
    }

    socialLogin(socialConnection: string): void {
        const redirectDomain = this.getRedirectDomain()
        this.auth.authorize({ 
            connection: socialConnection,
            responseType: environment.auth.loginResponseType,
            redirectUri: `${redirectDomain}/authenticate`
        })
    }

    logout(): void {
        this.endSession()
    }

    resetPassword(email: string, successCallback: Function) {
        this.auth.changePassword({
            connection: environment.auth.realm,
            email
          }, (err, resp) => {
            if (err) {
              console.error('AUTH: Something went wrong with reset password:', err);
              return;
            }
            successCallback()
            console.log('AUTH: Password reset email sent:', resp);
          });
    }

    authenticate(): void {
        const hash: string = this._activatedRoute.snapshot.fragment

        if (hash) {
            this.auth.parseHash({
                hash
            },
            function(error: Auth0ParseHashError | null, result: Auth0DecodedHash | null) {
                if (error) {
                    console.log('AUTH: Error while attempting to parse hash: ', error)
                    this.endSession()
                }
                else if (result && result.accessToken && result.idToken) {
                    console.log('AUTH: Parse hash success - access token received.')
                    this.accessToken = result.accessToken
                    this.authResult = result
                    this.fetchUser()
                }
            }.bind(this))
        }
    }

    fetchUser(): void {
        if (this.accessToken) {
            this.auth.client.userInfo(
                this.accessToken,
                function(error: Auth0Error, result: Auth0UserProfile) {
                    if (error) {
                        console.log('AUTH: Error while fetching profile: ', error)
                        this.endSession()
                    }
                    else {
                        console.log('AUTH: User profile: ', result)
                        this.userProfile = result
                        this.setSession()
                    }
                }.bind(this)
            )
        }
        else {
            console.log('AUTH: Cannot get user profile. No access token found')
        }
    }

    checksession(): void {
        this.auth.checkSession({responseType: environment.auth.loginResponseType}, function (err, authResult) {
            if (err) {
                console.log('AUTH: Could not get a new token', err)
                return
            }
            
            console.log('AUTH: Successfully refreshed token')
            this.authResult = authResult
            this.setSession()
        }.bind(this));
    }

    startRenewSessionTimer(): void {
        // Renew token on expiration
        if (this.tokenExpiration) {
            const time = getTimeDifferenceInSeconds(this.tokenExpiration)
            clearTimeout(this.renewSessionTimer)

            console.log('AUTH: Renew token in ' + time + ' seconds')

            this.renewSessionTimer = setTimeout(() => {
                this.checksession()
            }, time * 1000)
        }
    }

    setSession(): void {
        this.tokenExpiration = this.authResult.expiresIn * 1000 + new Date().getTime()

        if (this.sessionMaintained) {
            localStorage.setItem(accessTokenKey, this.authResult.accessToken)
            localStorage.setItem(idTokenKey, this.authResult.idToken)
            localStorage.setItem(expiresAtKey, JSON.stringify(this.tokenExpiration))
            localStorage.setItem(userProfileKey, JSON.stringify(this.userProfile))
        } else {
            sessionStorage.setItem(accessTokenKey, this.authResult.accessToken)
            sessionStorage.setItem(idTokenKey, this.authResult.idToken)
            sessionStorage.setItem(expiresAtKey, JSON.stringify(this.tokenExpiration))
            sessionStorage.setItem(userProfileKey, JSON.stringify(this.userProfile))
        }

        if (!this.authenticated) {
            this.markAsAuthenticated() 
        } else {
            // Reload after seession renewel
            window.location.reload()
        }
        
        if (this.sessionMaintained) {
            this.startRenewSessionTimer()
        }
    }

    endSession(): void {
        clearTimeout(this.renewSessionTimer)
        localStorage.removeItem(accessTokenKey)
        localStorage.removeItem(idTokenKey)
        localStorage.removeItem(expiresAtKey)
        localStorage.removeItem(userProfileKey)
        sessionStorage.removeItem(accessTokenKey)
        sessionStorage.removeItem(idTokenKey)
        sessionStorage.removeItem(expiresAtKey)
        sessionStorage.removeItem(userProfileKey)
        this.markAsAuthenticated(false)
    }

    maintainSession(value: boolean = false): void {
        this.sessionMaintained = value
        if (value) {
            localStorage.setItem(maintainSessionKey, 'true')
        } else {
            localStorage.removeItem(maintainSessionKey)
        }
    }

    getMaintainSession() {
        return localStorage.getItem(maintainSessionKey) === 'true'
    }

    tokenNotExpired(): boolean {
        const currentTime = new Date().getTime()
        return currentTime < this.tokenExpiration
    }

    isAuthenticated(): boolean {
        if (this.testToken) { return true }
        return !!this.accessToken && !!this.tokenExpiration && this.tokenNotExpired() && !!this.userProfile?.sub
    }

    isAuthenticatedAsync(): Promise<void>  {
        if (this.isAuthenticated()) {
            return Promise.resolve()
        }   
    }

    getUserProfile(): UserProfile {
        return this.userProfile ? {
            countryCode: this.userProfile['https://jointheflok.com/geoip']?.country_code.toLowerCase(),
            email: this.userProfile.email,
            firstName: this.userProfile.given_name,
            lastName: this.userProfile.family_name,
            sub: this.userProfile.sub
        } : undefined
    }

    getAccessToken(): string {
        return this.accessToken ?? this.testToken
    }

    async accessTokenAsync(): Promise<string> {
        if (this.testToken) {
            return new Promise(resolve => resolve(this.testToken))
        }
        return new Promise(resolve => resolve(this.accessToken))
    }

    private markAsAuthenticated(authenticated = true): void {
        this.authenticated = authenticated
        this.userProfile = authenticated ? this.userProfile : null
        this.tokenExpiration = authenticated ? this.tokenExpiration : null
        console.log('AUTH: authenticated:', authenticated)
        console.log('AUTH: user profile: ', this.userProfile)
        this.authenticatedSubject.next(authenticated)
    }
}